import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import Card from '../components/common/Card'
import ProfilePhotoField from '../components/social/ProfilePhotoField'
import { faDigits } from '../lib/numerals'
import { isValidJalali } from '../lib/jalali'
import {
  NAME_CHECK_DELAY_MS,
  PROFILE_ITEMS,
  formFromProfile,
  jalaliDayLabel,
  nameBadge,
  persianMonths,
  profileCompletion,
  stepYear,
  updateBody,
  updateFailureOf,
} from '../lib/profileForm'
import { displayNameAvailable, updateProfile } from '../services/auth'
import { useAuthStore } from '../store/authStore'

const BADGE_TONE = {
  good: 'bg-good-tint text-good-ink',
  warn: 'bg-warn-tint text-warn-ink',
  bad: 'bg-bad-tint text-bad-ink',
  muted: 'bg-surface-2 text-ink-3',
}

const NOTICE_TONE = {
  warn: 'bg-warn-tint text-warn-ink',
  bad: 'bg-bad-tint text-bad-ink',
}

const INPUT =
  'h-14 w-full rounded-[14px] border-[1.5px] border-line bg-surface px-3.5 text-[15px] font-semibold text-ink outline-none transition focus:border-brand-500'

function FieldLabel({ children }) {
  return <span className="text-[12.5px] font-semibold text-ink-2">{children}</span>
}

function Helper({ children, tone = 'muted' }) {
  return <p className={`text-[11px] leading-[1.85] ${tone === 'warn' ? 'text-warn-ink' : 'text-ink-3'}`}>{children}</p>
}

/** The 26px ± pair of the date boxes. */
function MiniStepper({ value, onUp, onDown, upLabel, downLabel, minWidth }) {
  const button = 'grid size-[26px] place-items-center rounded-[7px] bg-surface-2 text-sm font-bold text-ink-2'
  return (
    <div className="flex items-center gap-1">
      <button type="button" aria-label={upLabel} onClick={onUp} className={button}>
        +
      </button>
      <span className="text-center text-[15px] font-bold text-ink" style={{ minWidth }}>
        {value}
      </span>
      <button type="button" aria-label={downLabel} onClick={onDown} className={button}>
        −
      </button>
    </div>
  )
}

function DateBox({ label, className = '', children, ...props }) {
  return (
    <div
      className={`flex flex-col items-center gap-1.5 rounded-[14px] border-[1.5px] border-line bg-surface px-2.5 py-2 ${className}`}
      {...props}
    >
      <span className="text-[10.5px] text-ink-3">{label}</span>
      <div className="grid h-[26px] place-items-center">{children}</div>
    </div>
  )
}

/**
 * Profile completion (design page 14, `editProfile`; P-editProfile of the
 * go-live plan). Saved to the server (`me_updateProfile`, B3) and opened
 * from the profile the auth store already holds. The birthday is collected
 * in Jalali and crosses the wire in Gregorian (`lib/jalali.js`).
 *
 * The «آزاد است» badge is the server's answer, asked a moment after typing
 * stops. The 30-day rule is the server's too: a locked name being changed
 * shows the day the lock lifts, and a refused save shows the server's
 * reason. The save button always answers a tap — complete, it saves;
 * incomplete, it says what is missing; a day that is not in the calendar
 * says so — which is why it is not a disabled button.
 */
export default function EditProfile() {
  const { t } = useTranslation('editProfile')
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  return (
    <EditProfileForm
      user={user}
      onSaved={(updated) => {
        setUser(updated)
        navigate('/profile', { state: { toast: t('page.savedToast') } })
      }}
      onNothingToSave={() => navigate('/profile')}
      onPhotoChanged={setUser}
    />
  )
}

/**
 * The form itself, with the profile handed in — so it renders the same
 * from the store and from a test, and the store is the page's business.
 */
export function EditProfileForm({ user, onSaved, onNothingToSave, onPhotoChanged = () => {} }) {
  const { t } = useTranslation('editProfile')
  const [form, setForm] = useState(() => formFromProfile(user))
  const [monthOpen, setMonthOpen] = useState(false)
  const [check, setCheck] = useState(null)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState(null)

  const FAILURE_COPY = {
    offline: t('failure.offline'),
    name_taken: t('failure.nameTaken'),
    name_locked: t('failure.nameLocked'),
    name_invalid: t('failure.nameInvalid'),
    date_invalid: t('failure.dateInvalid'),
    service: t('failure.service'),
  }

  const patch = (changes) => setForm((current) => ({ ...current, ...changes }))
  const touchBirth = (changes) => patch({ ...changes, birthSet: true, birthTouched: true })

  // The badge: asked once typing has paused, so a name is not checked
  // letter by letter. Null while unknown (blank, still typing, offline).
  useEffect(() => {
    setCheck(null)
    const candidate = form.displayName.trim()
    if (!candidate) return undefined
    let cancelled = false
    const timer = setTimeout(() => {
      displayNameAvailable(candidate)
        .then((result) => {
          if (!cancelled) setCheck(result)
        })
        .catch(() => {})
    }, NAME_CHECK_DELAY_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [form.displayName])

  const percent = profileCompletion(form.fullName, form.displayName)
  const complete = percent === 100
  const left = PROFILE_ITEMS - Math.round((percent * PROFILE_ITEMS) / 100)
  const nameChanged = form.displayName.trim() !== (user?.displayName ?? '')
  const lockLabel = user?.displayNameLockedUntil ? jalaliDayLabel(user.displayNameLockedUntil) : null
  const badge = nameBadge(!form.displayName.trim(), check)

  async function save() {
    if (saving) return
    if (!complete) {
      setNotice({ tone: 'warn', text: t('notice.incomplete') })
      return
    }
    // The steppers can name 31 مهر; the calendar cannot. Only a date the learner edited is checked.
    if (form.birthTouched && !isValidJalali(form.year, form.month, form.day)) {
      setNotice({ tone: 'warn', text: FAILURE_COPY.date_invalid })
      return
    }
    const body = updateBody(form, user)
    if (Object.keys(body).length === 0) {
      onNothingToSave()
      return
    }
    setSaving(true)
    setNotice(null)
    try {
      onSaved(await updateProfile(body))
    } catch (error) {
      setNotice({ tone: 'bad', text: FAILURE_COPY[updateFailureOf(error)] })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-2">
        <Link to="/profile" aria-label={t('page.backToProfile')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
        <h1 className="flex-1 text-lg font-bold text-ink">{t('page.title')}</h1>
      </header>

      {/* completion meter */}
      <Card as="div" pad="p-4" className="flex items-center gap-3">
        <span aria-hidden="true" className="grid size-[54px] shrink-0 place-items-center rounded-full border-[2.5px] border-brand-600 bg-brand-50 text-2xl">
          🦜
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="flex-1 text-[13px] font-bold text-ink">{t('meter.label', { percent: faDigits(percent) })}</span>
            <span className="text-[11px] text-ink-3">{complete ? t('meter.complete') : t('meter.remaining', { count: faDigits(left) })}</span>
          </div>
          <div role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} className="h-[7px] overflow-hidden rounded-full bg-track">
            <div className="h-full rounded-full bg-brand-600 transition-[width] duration-200" style={{ width: `${percent}%` }} />
          </div>
        </div>
      </Card>

      {/* profile photo (V5-w) — nothing at all while photos are switched off */}
      <ProfilePhotoField user={user} onChanged={onPhotoChanged} />

      {/* full name */}
      <label className="flex flex-col gap-2">
        <FieldLabel>{t('field.fullNameLabel')}</FieldLabel>
        <input value={form.fullName} onChange={(e) => patch({ fullName: e.target.value })} placeholder={t('field.fullNamePlaceholder')} className={INPUT} />
        <Helper>{t('field.fullNameHelper')}</Helper>
      </label>

      {/* birth date */}
      <div className="relative flex flex-col gap-2">
        <FieldLabel>{t('field.birthDateLabel')}</FieldLabel>
        <div className="flex gap-1.5">
          <DateBox label={t('field.dayLabel')} className="flex-1">
            <MiniStepper
              value={faDigits(form.day)}
              upLabel={t('field.dayNext')}
              downLabel={t('field.dayPrev')}
              minWidth={28}
              onUp={() => touchBirth({ day: form.day === 31 ? 1 : form.day + 1 })}
              onDown={() => touchBirth({ day: form.day === 1 ? 31 : form.day - 1 })}
            />
          </DateBox>
          <DateBox label={t('field.monthLabel')} className="flex-[1.4] cursor-pointer" role="button" tabIndex={0} aria-expanded={monthOpen} onClick={() => setMonthOpen((open) => !open)} onKeyDown={(e) => e.key === 'Enter' && setMonthOpen((open) => !open)}>
            <span className="flex items-center gap-1.5 text-[15px] font-bold text-ink">
              {persianMonths()[form.month - 1]}
              <span aria-hidden="true" className={`text-[8px] text-ink-3 transition-transform ${monthOpen ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </span>
          </DateBox>
          <DateBox label={t('field.yearLabel')} className="flex-[1.2]">
            <MiniStepper
              value={faDigits(form.year)}
              upLabel={t('field.yearNext')}
              downLabel={t('field.yearPrev')}
              minWidth={42}
              onUp={() => touchBirth({ year: stepYear(form.year, 1) })}
              onDown={() => touchBirth({ year: stepYear(form.year, -1) })}
            />
          </DateBox>
        </div>
        <Helper>
          {form.birthSet
            ? t('field.birthHelperSet', { day: faDigits(form.day), month: persianMonths()[form.month - 1], year: faDigits(form.year) })
            : t('field.birthHelperUnset')}
        </Helper>
        {monthOpen && (
          <div role="listbox" aria-label={t('field.monthLabel')} className="absolute inset-x-0 top-[104px] z-20 grid grid-cols-2 gap-1 rounded-[14px] border-[1.5px] border-line bg-surface p-1.5 shadow-lg">
            {persianMonths().map((name, index) => {
              const active = index + 1 === form.month
              return (
                <button
                  key={name}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    touchBirth({ month: index + 1 })
                    setMonthOpen(false)
                  }}
                  className={`h-[42px] rounded-[10px] text-[13px] font-semibold ${active ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface-2 text-ink'}`}
                >
                  {name}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* display name */}
      <div className="flex flex-col gap-2">
        <FieldLabel>{t('field.displayNameLabel')}</FieldLabel>
        <div className="flex h-14 items-center gap-2.5 rounded-[14px] border-[1.5px] border-line bg-surface px-3.5 focus-within:border-brand-500">
          <input
            value={form.displayName}
            onChange={(e) => patch({ displayName: e.target.value })}
            placeholder={t('field.displayNamePlaceholder')}
            aria-label={t('field.displayNameLabel')}
            className="h-full min-w-0 flex-1 bg-transparent text-[15px] font-semibold text-ink outline-none"
          />
          <span role="status" className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${BADGE_TONE[badge.tone]}`}>
            {badge.label}
          </span>
        </div>
        <Helper>{t('field.displayNameHelper')}</Helper>
        {lockLabel && nameChanged && <Helper tone="warn">{t('field.displayNameLocked', { lockLabel })}</Helper>}
      </div>

      {notice && (
        <p role="alert" className={`rounded-xl px-3 py-2 text-xs ${NOTICE_TONE[notice.tone]}`}>
          {notice.text}
        </p>
      )}

      {/* save — always answers, as the design asks */}
      <button
        type="button"
        onClick={save}
        aria-busy={saving || undefined}
        className={`mt-0.5 h-14 w-full rounded-[14px] text-[15.5px] font-bold transition-colors ${
          complete && !saving ? 'bg-brand-600 text-white hover:bg-brand-700 dark:text-slate-900' : 'bg-surface-2 text-ink-3'
        }`}
      >
        {saving ? t('save.saving') : t('save.label')}
      </button>
    </div>
  )
}
